import React from "react";
import { shallowEqual } from "react-redux";
import { CircleMarker, Polyline, Popup, Tooltip, useMap } from "react-leaflet";
import { LatLngBoundsExpression, Map } from "leaflet";

import { useAppSelector } from "../../redux/hooks";
import { ContainerPopup, TooltipContainer } from "./BlockWithTripsPunctualityAndOccupancyReportMap";
import { PopupContainer } from "../views/SelectedBusViewMap";
import { useTranslation } from "react-i18next";
import { stopsOptionsConstant } from "../../data/constants";
import { getWheelColors } from "../../utils/helperWheelColors";
import { getAverageDeviationBackgroundFormat, getBusFillingColor } from "../../utils/helpers";

const TripAveragePaxReportMap = (): JSX.Element => {
  const leafletMap: Map = useMap();

  const [averageTripData, routesInfo, allStopsArrayFiltered]: [
    {
      pathPoints: [number, number][];
      bounds: [number, number][];
      tripData: { [key: string]: string };
      averageData: AveragePaxCountTripData[];
    } | null,
    RouteDetails[],
    StopInfo[],
  ] = useAppSelector(
    (state: RootState) => [
      state?.mapView?.averageTripData,
      state?.routesInfo?.routesInfo,
      state?.routesInfo?.allStopsArrayFiltered,
    ],
    shallowEqual
  );

  React.useEffect(() => {
    if (averageTripData && Object.keys(averageTripData)?.length && leafletMap) {
      const { bounds } = averageTripData;
      // console.log("bounds:", bounds);
      leafletMap.fitBounds(bounds as LatLngBoundsExpression);
    }
  }, [averageTripData, leafletMap]);

  const currentRouteColor = routesInfo?.find((route: RouteDetails) => route?.id === averageTripData?.tripData?.routeId)
    ?.color as string;

  const currentTripData = {
    ...averageTripData?.tripData,
    color: currentRouteColor,
    acronym: routesInfo?.find((route: RouteDetails) => route?.id === averageTripData?.tripData?.routeId)?.acronym || "",
  };

  const avgTripDataForStops = averageTripData?.averageData?.map((elem: AveragePaxCountTripData) => {
    return {
      ...elem,
      lat: allStopsArrayFiltered?.find((stop: StopInfo) => stop?.stopId === elem?.stop_id)?.lat as number,
      lon: allStopsArrayFiltered?.find((stop: StopInfo) => stop?.stopId === elem?.stop_id)?.lon as number,
    };
  }) as AveragePaxCountTripData[];

  return (
    <React.Fragment>
      {avgTripDataForStops &&
      currentTripData &&
      currentRouteColor &&
      averageTripData &&
      Object.keys(averageTripData)?.length &&
      routesInfo ? (
        <React.Fragment>
          <Polyline
            positions={averageTripData?.pathPoints}
            pathOptions={{ color: currentRouteColor, weight: 4 }}
            children={<Popup>{<ContainerPopup tripData={currentTripData} />}</Popup>}
          />

          <TripStops tripAverageData={avgTripDataForStops} color={currentRouteColor || "#000000"} />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default TripAveragePaxReportMap;

//* Trip Stops
const TripStops = ({
  tripAverageData,
  color,
}: {
  tripAverageData: AveragePaxCountTripData[];
  color: string;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      {
        tripAverageData?.map(
          (elem: AveragePaxCountTripData, index: number, array: AveragePaxCountTripData[]): JSX.Element => (
            <CircleMarker
              pathOptions={{
                ...stopsOptionsConstant,
                color: getWheelColors(color, 3)?.[1],
                fillColor: getWheelColors(color, 3)?.[2],
              }}
              key={String(index + elem?.stop_id + elem?.stopname)}
              center={{
                lat: elem?.lat as number,
                lng: elem?.lon as number,
              }}
              radius={6}
            >
              <Tooltip interactive={true} permanent={true} direction={index % 2 === 0 ? "right" : "left"}>
                <TooltipContainer>
                  <div>
                    {t("Id")} <span className="span_maroon span_bold">{elem?.stop_id}</span>{" "}
                    {`(${index + 1}/${array.length})`}
                  </div>
                  <div
                    className="span_bold"
                    style={{
                      backgroundColor: getAverageDeviationBackgroundFormat(elem?.avg_delay)?.background,
                    }}
                  >
                    {getAverageDeviationBackgroundFormat(elem?.avg_delay)?.value}
                  </div>
                  <div
                    className="span_bold"
                    style={{
                      backgroundColor: getBusFillingColor(elem?.avg_passenger_fullness),
                      textAlign: "center",
                    }}
                  >
                    {elem?.avg_passenger_fullness ? `${Number(elem?.avg_passenger_fullness).toFixed(0)}%` : t("b/d")}
                  </div>
                </TooltipContainer>
              </Tooltip>
              <Popup>
                <PopupContainer>
                  <div className="popup-line">
                    {t("Nazwa")} <span className="span_maroon">{elem?.stopname}</span>
                  </div>
                  <div className="popup-line">
                    {t("Id")} <span className="span_maroon">{elem?.stop_id}</span>
                  </div>
                  <div className="popup-line text-muted small">
                    {t("Pozycja")}: <span className="span_bold text-muted">{`${elem?.lat}; ${elem?.lon}`}</span>
                  </div>
                </PopupContainer>
              </Popup>
            </CircleMarker>
          )
        ) as JSX.Element[]
      }
    </React.Fragment>
  );
};
